import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild, inject } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, map } from 'rxjs';
import { ButtonModule, ModalComponent, ModalModule, SelectionControlsModule, TA_MODAL_DATA } from 'teccom-web-components';
import { TermsOfUseKeys, termsOfUseFallback } from './terms-of-use.texts';

/** `onChange` payload of `ta-checkbox` (not exported by teccom-web-components). */
interface CheckboxChange {
  checked: boolean;
}

export interface TermsOfUseDialogData {
  /** HTML of the current Terms of Use / Privacy Policy, delivered by the backend. */
  content: string;
  /** Records the acceptance; the dialog closes once it succeeded. */
  accept: () => Observable<unknown>;
}

/**
 * Blocking Terms of Use dialog (no close icon, no backdrop dismissal): the user can only accept.
 * The content is bound with `[innerHTML]` and therefore sanitized by Angular, as in Legacy.
 */
@Component({
  selector: 'app-terms-of-use-dialog',
  standalone: true,
  imports: [AsyncPipe, TranslateModule, ModalModule, ButtonModule, SelectionControlsModule],
  templateUrl: './terms-of-use-dialog.component.html',
  styleUrl: './terms-of-use-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TermsOfUseDialogComponent {
  readonly data = inject<TermsOfUseDialogData>(TA_MODAL_DATA);
  private readonly translate = inject(TranslateService);
  private readonly cd = inject(ChangeDetectorRef);

  @ViewChild(ModalComponent) private modal?: ModalComponent;

  readonly checkLabel$ = this.text('checkAccept');
  readonly acceptLabel$ = this.text('buttonAccept');

  accepted = false;
  saving = false;

  onAcceptedChange(change: CheckboxChange): void {
    this.accepted = change.checked;
  }

  accept(): void {
    if (!this.accepted || this.saving) {
      return;
    }
    this.saving = true;
    this.data.accept().subscribe({
      next: () => this.modal?.closeComponent(),
      // As in Legacy the dialog stays open, so the user can simply try again
      error: () => {
        this.saving = false;
        this.cd.markForCheck();
      }
    });
  }

  /** Translation of the Shell key, the Legacy text as long as the key is not maintained. */
  private text(text: keyof typeof TermsOfUseKeys): Observable<string> {
    const key = TermsOfUseKeys[text];
    return this.translate.stream(key).pipe(
      map((translation: string) => translation && translation !== key ? translation : termsOfUseFallback(text, this.translate.currentLang))
    );
  }
}
